import { formatAmounts, collator } from '@aytm/app/stats/lib/util/helpers'
import { LINK_ID_REG } from '@aytm/app/stats/page/question-views/components/lib/constants'
import { processText, questionIdToPosition } from '@aytm/app/stats/page/components/lib/utils'
import { openCommentsPanel } from '@aytm/app/stats/lib/stat-page'
import { applySorted, calcCommentsQuestion } from '@aytm/app/stats/page/question-views/oe/lib/table-utils'
import { sortRows } from '@aytm/app/stats/components/data-views/table/components/Table'

const sortByTitle = (aRow, bRow, dir) => dir * collator.compare(aRow.cells[0].title, bRow.cells[0].title)

const sortByPercents = (aRow, bRow, dir) => dir * (aRow.cells[1].percents - bRow.cells[1].percents)

const showComments = features => features?.individual_data?.status?.oe !== 'off'

const clusterChip = cluster => ({ type: 'cluster', qid: cluster.qid, value: cluster.title, label: cluster.title })

// Under the cluster mask a hand-off that names no cluster of its own is narrowed to the masked
// clusters: the configured ones, or every cluster of the question when none is configured.
const maskedClusterChips = (clusters, config) => {
  if (!config?.applyClustersMask) return []

  const masked = config.clusters ? clusters.filter(c => config.clusters.includes(c.uid)) : clusters
  return masked.filter(c => c?.title).map(clusterChip)
}

export const buildOnCommentsClick = ({ id, questionId, link, isReportXpert, statType, batches, clusters, config }) => {
  return (event, position) => {
    const q = isReportXpert && LINK_ID_REG.test(link || '') ? questionIdToPosition(link) : questionId || id

    const path = calcCommentsQuestion(q, id)

    const query = []

    if (statType === 'pins') query.push(...maskedClusterChips(clusters, config))

    if (statType === 'pins' && position >= 0) {
      const batch = batches.find(b => b.position === position)
      if (batch) {
        query.push({
          type: 'batch',
          path: `${path}.sq${position}`,
          color: batch.color,
          label: `${path.toUpperCase()}.SQ${position}: ${batch.text}`,
          value: batch.text
        })
      }
    }

    if (statType === 'clusters') {
      const cluster = clusters.filter(c => !c.off)[position - 1]
      if (cluster?.title) query.push(clusterChip(cluster))
    }

    openCommentsPanel({ event, path, query, isReportXpert })
  }
}

// The comments links on the chart's popups. A pin's names its respondent and its batch, a cluster's names the
// cluster; either opens the panel narrowed the way the chart reads, through the cluster mask when one is applied.
export const buildOnChartCommentsClick = ({ id, questionId, link, isReportXpert, batches, clusters, config }) => {
  return (event, { responseId, batchPosition, clusterId } = {}) => {
    const q = isReportXpert && LINK_ID_REG.test(link || '') ? questionIdToPosition(link) : questionId || id

    const path = calcCommentsQuestion(q, id)

    const query = []
    const pushBatch = position => {
      const batch = batches.find(b => b.position === position)
      if (!batch?.comments) return
      query.push({
        type: 'batch',
        path: `${path}.sq${position}`,
        color: batch.color,
        label: `${path.toUpperCase()}.SQ${position}: ${batch.text}`,
        value: batch.text
      })
    }
    if (clusterId) {
      const cluster = clusters.find(c => c.uid === clusterId)
      if (cluster?.title) query.push(clusterChip(cluster))
      for (const b of config?.batches || []) pushBatch(Number(b.split('b')[1]))
    } else {
      query.push(...maskedClusterChips(clusters, config))
      if (responseId) query.push({ type: 'uid', value: responseId, label: String(responseId) })
      if (batchPosition) pushBatch(Number(batchPosition))
    }

    openCommentsPanel({ event, path, query, isReportXpert })
  }
}

export const calcPinsTableData = ({ answer, batches, features, reportPercentDecimals, onCommentsClick }) => {
  const isLoading = 'loading' === answer

  const items = batches.reduce((acc, cur) => {
    const a = (!isLoading && answer?.[`b${cur.position}`]) || {}

    const { amount, comments } = cur.answerVariants.reduce(
      (acc, cur) => {
        const av = a[`a${cur.position}`] || {}

        return {
          amount: acc.amount + (av.amount || 0),
          comments: acc.comments + (av.comments || 0)
        }
      },
      { amount: 0, comments: 0 }
    )

    acc[`b${cur.position}`] = { amount, comments }

    return acc
  }, {})

  const total = Object.values(items).reduce((acc, cur) => acc + cur.amount, 0)

  const cols = [
    { type: 'title', title: 'Pins', sort: sortByTitle },
    {
      type: 'progress',
      title: `Responses:<small>${formatAmounts(total)}</small>`,
      percentDecimals: reportPercentDecimals,
      sort: sortByPercents
    }
  ]

  // if (showComments(features) && answer?.comments > 0) {
  //   cols[0].comments = answer.comments
  //   cols[0].onCommentsClick = onCommentsClick
  // }

  const rows = batches.map(b => {
    const item = items[`b${b.position}`] || {}

    const titleCell = {
      // @ts-ignore
      title: processText(b.text),
      colorIndex: ((b.position - 1) % 21) + 1
    }

    // The pin's own flag decides the block: the stats service counts stored text whatever the flag
    // says. A flagged pin with none reads a greyed, unlinked 0 -- but only once the answer is here:
    // before that there is no count to read, and a 0 would claim one.
    if (showComments(features) && b.comments && !isLoading && answer) {
      titleCell.comments = item.comments || 0
      if (item.comments > 0) titleCell.onCommentsClick = onCommentsClick
    }

    return {
      cells: [titleCell, { amount: item.amount || 0, percents: (item.amount * 100) / total || 0, loading: isLoading }],
      position: b.position
    }
  })

  return { cols, rows }
}

export const calcClustersTableData = ({ answer, clusters, features, reportPercentDecimals, onCommentsClick }) => {
  const isLoading = 'loading' === answer
  const filteredClusters = clusters.filter(c => !c.off)
  const total = filteredClusters.flatMap(c => c.points || []).filter((x, i, a) => i === a.indexOf(x)).length

  const cols = [
    { type: 'title', title: 'Clusters', sort: sortByTitle },
    {
      type: 'progress',
      title: `Responses:<small>${formatAmounts(total)}</small>`,
      percentDecimals: reportPercentDecimals,
      sort: sortByPercents
    }
  ]

  const rows = filteredClusters.map((cluster, i) => {
    const amount = cluster.stat?.points || 0
    const percents = Math.round((amount * 100) / total) || 0

    const titleCell = {
      // @ts-ignore
      title: processText(cluster.title),
      colorIndex: (i % 21) + 1
    }

    if (showComments(features) && (cluster.stat?.comments || 0) > 0) {
      titleCell.comments = cluster.stat.comments
      titleCell.onCommentsClick = onCommentsClick
    }

    return {
      cells: [titleCell, { amount, percents, loading: isLoading }],
      position: i + 1
    }
  })

  return { cols, rows }
}

// The drawn clusters' uids in the order the clusters table shows them. The summary and pptx exports
// build their cluster table only from the clusters named here, in this order, so it follows the
// screen: the same rows, sorted by the table's own row sort.
export const calcClustersSortOrder = ({ clusters = [], sortOrder }) => {
  const drawn = clusters.filter(c => !c.off)
  const { cols, rows } = calcClustersTableData({ answer: null, clusters: drawn })

  return sortRows(rows, applySorted(cols, sortOrder)).map(row => drawn[row.position - 1].uid)
}

export const calcTableData = ({
  statType,
  answer,
  batches,
  clusters,
  features,
  reportPercentDecimals,
  onCommentsClick
}) => {
  if (statType === 'pins') {
    return calcPinsTableData({ answer, batches, features, reportPercentDecimals, onCommentsClick })
  }

  if (statType === 'clusters') {
    return calcClustersTableData({ answer, clusters, features, reportPercentDecimals, onCommentsClick })
  }

  return { cols: [], rows: [] }
}
